import React, { useEffect, useState } from "react";
import { useAuth } from "@/lib/auth";
import { api, BRAND, formatApiError } from "@/lib/api";
import { PageHeader, StatCard } from "@/components/Common";
import { Percent, Star, Wallet, Megaphone, User } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { CarteirinhaButton } from "@/components/CarteirinhaDialog";
import { OverallStickerButton } from "@/components/OverallStickerDialog";

export default function StudentHome() {
  const { user } = useAuth();
  const [student, setStudent] = useState(null);
  const [att, setAtt] = useState(null);
  const [evals, setEvals] = useState([]);
  const [pays, setPays] = useState([]);
  const [announcements, setAnnouncements] = useState([]);

  useEffect(() => {
    if (!user?.student_id) return;
    const sid = user.student_id;
    (async () => {
      try {
        const [s, a, e, p, an] = await Promise.all([
          api.get(`/students/${sid}`),
          api.get(`/attendance/student/${sid}`),
          api.get(`/evaluations/student/${sid}`),
          api.get(`/payments/student/${sid}`),
          api.get("/announcements"),
        ]);
        setStudent(s.data); setAtt(a.data); setEvals(e.data); setPays(p.data);
        setAnnouncements(an.data.slice(0, 3));
      } catch (err) { toast.error(formatApiError(err)); }
    })();
  }, [user]);

  const latestPayment = pays[0];
  const allScores = evals.flatMap(e => Object.values(e.scores || {}));
  const avgScore = allScores.length ? (allScores.reduce((a, b) => a + b, 0) / allScores.length).toFixed(1) : "—";
  const latestEvalAvg = evals.length
    ? (Object.values(evals[evals.length - 1].scores || {}).reduce((a, b) => a + b, 0) /
       Object.values(evals[evals.length - 1].scores || {}).length).toFixed(1)
    : "—";

  return (
    <div data-testid="student-home-page">
      <div className="vv-card p-5 sm:p-6 mb-8 relative overflow-hidden">
        <div className="flex flex-wrap justify-end gap-2 mb-4">
          {student && (
            <>
              <OverallStickerButton student={student} evaluations={evals} />
              <CarteirinhaButton
                student={student}
                stats={{ attendance: att?.percentage ?? 0, evals: evals.length, avgScore }}
              />
            </>
          )}
        </div>
        <div className="flex items-center gap-4 sm:gap-5 flex-wrap sm:flex-nowrap">
          <div className="size-20 sm:size-24 rounded-2xl overflow-hidden border-4 border-[hsl(var(--vv-lime))/0.5] shadow-lg shrink-0">
            {student?.photo_url ? (
              <img src={student.photo_url} alt={student.name} className="w-full h-full object-cover" data-testid="student-home-photo" />
            ) : (
              <div className="w-full h-full flex items-center justify-center bg-muted">
                <User className="size-10 sm:size-12 text-muted-foreground" />
              </div>
            )}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[11px] uppercase tracking-[0.3em] text-[hsl(var(--vv-lime))]">Bem-vindo</div>
            <h1 className="font-black leading-tight">
              <span className="block text-2xl sm:text-3xl">{student?.first_name || (user?.name?.split(" ")[0] ?? "")}</span>
              {(student?.last_name || (user?.name?.split(" ").slice(1).join(" "))) && (
                <span className="block text-sm sm:text-base font-semibold text-muted-foreground mt-0.5 truncate">
                  {student?.last_name || user?.name?.split(" ").slice(1).join(" ")}
                </span>
              )}
            </h1>
            <p className="text-muted-foreground mt-2 text-sm whitespace-nowrap overflow-hidden text-ellipsis">
              {student ? `${student.category} • ${student.position || "Sem posição"}` : "Carregando perfil..."}
            </p>
          </div>
          <img src={BRAND.LOGO} alt="" className="h-14 hidden md:block vv-logo-glow shrink-0" />
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <StatCard label="Minha presença" value={`${att?.percentage ?? 0}%`} icon={Percent} color="lime" to="/aluno/presenca" testId="stat-my-attendance" />
        <StatCard label="Avaliações" value={evals.length} icon={Star} color="yellow" to="/aluno/avaliacoes" testId="stat-my-evals" />
        <StatCard label="Última nota média" value={latestEvalAvg} icon={Star} color="blue" to="/aluno/evolucao" testId="stat-my-avg" />
        <StatCard label="Pagamento atual" value={latestPayment ? latestPayment.status : "—"} icon={Wallet} color={latestPayment?.status === "pago" ? "lime" : "yellow"} to="/aluno/financeiro" testId="stat-my-finance" />
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="vv-card p-5 lg:col-span-2">
          <div className="text-xs uppercase tracking-widest text-[hsl(var(--vv-lime))]">Meus dados</div>
          <h3 className="text-xl font-black mb-3">Perfil</h3>
          {student ? (
            <dl className="grid sm:grid-cols-2 gap-3 text-sm">
              <Info label="Nome" value={student.name} />
              <Info label="Email" value={student.email} />
              <Info label="Nascimento" value={student.birth_date} />
              <Info label="CPF" value={student.cpf || "—"} />
              <Info label="Categoria" value={student.category} />
              <Info label="Posição" value={student.position || "—"} />
              <Info label="Telefone" value={student.phone || "—"} />
              <Info label="Responsável" value={student.guardian || "—"} />
              <Info label="Tel. responsável" value={student.guardian_phone || "—"} />
              <Info label="Status" value={student.status} />
            </dl>
          ) : <div className="text-muted-foreground">Carregando...</div>}
        </div>

        <div className="vv-card p-5">
          <div className="text-xs uppercase tracking-widest text-[hsl(var(--vv-lime))]">Mural</div>
          <h3 className="text-xl font-black mb-3">Avisos recentes</h3>
          <div className="space-y-3">
            {announcements.length === 0 && <div className="text-sm text-muted-foreground">Nenhum aviso.</div>}
            {announcements.map(a => (
              <div key={a.id} className="p-3 bg-muted/40 rounded-lg border border-border">
                <div className="font-semibold flex items-center gap-2"><Megaphone className="size-4 text-[hsl(var(--vv-yellow))]" />{a.title}</div>
                <div className="text-xs text-muted-foreground">{new Date(a.created_at).toLocaleDateString("pt-BR")}</div>
                <div className="text-sm mt-1 text-muted-foreground line-clamp-2">{a.description}</div>
              </div>
            ))}
            <Link to="/aluno/avisos" className="text-sm text-[hsl(var(--vv-lime))] hover:underline">Ver todos →</Link>
          </div>
        </div>
      </div>
    </div>
  );
}

function Info({ label, value }) {
  return (
    <div className="flex items-center justify-between border-b border-border/60 py-1.5">
      <dt className="text-muted-foreground text-xs uppercase tracking-wider">{label}</dt>
      <dd className="font-semibold">{value}</dd>
    </div>
  );
}
